import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowRight, Loader2, ShieldCheck } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { PageContainer } from '../../../common/components/PageContainer';
import { SidePanel } from '../../../components/design-system/common/SidePanel';
import { RelativeTime } from '../../../components/design-system/admin/RelativeTime';
import { ArdiChannel, ChannelKind, useArdiApi } from '../../../common/hooks/use-ardi-api.hook';
import { readApiError } from '../../../common/utils/api-error';
import { CHANNEL_ICONS, CHANNEL_KINDS, ChannelPhase, channelKindKey, channelPhase } from '../../../modules/vendor/channels/channel-presentation';
import { ChannelSetup } from '../../../modules/vendor/channels/ChannelSetup';
import { ChannelManage } from '../../../modules/vendor/channels/ChannelManage';

const PHASE_BADGE: Record<ChannelPhase, { variant: 'outline' | 'secondary'; dot?: string }> = {
  live: { variant: 'outline', dot: 'bg-success' },
  needs_pairing: { variant: 'outline', dot: 'bg-warning' },
  setup: { variant: 'secondary' },
  error: { variant: 'outline', dot: 'bg-destructive' },
};

/**
 * Where Ardi can be reached besides this backoffice.
 *
 * The chat apps a team already lives in — WhatsApp, Telegram, Slack — and a
 * plain webhook for everything else. Each is two things at once: a way to ask
 * Ardi ("how did last week go?") and a place Ardi posts what it notices. Both
 * are the same agent as Home, with the same approvals: a change Ardi proposes
 * in a chat still waits for someone to approve it here.
 */
const ChannelsPage: React.FC = () => {
  const { t } = useTranslation();
  const api = useArdiApi();
  const [params, setParams] = useSearchParams();
  const [channels, setChannels] = useState<ArdiChannel[] | null>(null);
  const [creating, setCreating] = useState<ChannelKind | null>(null);
  const openUuid = params.get('channel');

  const load = () => api.listChannels().then((r) => setChannels(r.channels)).catch(() => setChannels([]));

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A code is sent from the app, not from here: while one is waiting, look
  // for the chat that used it, so the panel shows it linked without a reload.
  const open = channels?.find((c) => c.uuid === openUuid) ?? null;
  useEffect(() => {
    if (!open?.pairing) return;
    const id = setInterval(() => {
      api.listChannels().then((r) => setChannels(r.channels)).catch(() => undefined);
    }, 4000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open?.uuid, open?.pairing?.code]);

  const openChannel = (uuid: string | null) => {
    const next = new URLSearchParams(params);
    if (uuid) next.set('channel', uuid);
    else next.delete('channel');
    setParams(next, { replace: true });
  };

  const replace = (c: ArdiChannel) => setChannels((list) => list?.map((x) => (x.uuid === c.uuid ? c : x)) ?? [c]);

  const add = async (kind: ChannelKind) => {
    setCreating(kind);
    try {
      const created = await api.createChannel(kind, t(channelKindKey(kind)) as string);
      setChannels((list) => [...(list ?? []), created]);
      openChannel(created.uuid);
    } catch (err) {
      const body = await readApiError(err);
      toast.error(body.message ? String(body.message) : t('ARDI.NOTIFICATIONS.SAVE_FAILED'));
    } finally {
      setCreating(null);
    }
  };

  const byKind = useMemo(() => new Map(CHANNEL_KINDS.map((k) => [k, (channels ?? []).filter((c) => c.kind === k)])), [channels]);

  return (
    <PageContainer breadcrumbs={[{ label: t('AGENTS.NAV.HOME', 'Home'), href: '/home/dashboard' }, { label: t('ARDI.CHANNELS.TITLE') }]}>
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-8">
        <div>
          <h1 className="font-heading text-2xl font-medium tracking-tight">{t('ARDI.CHANNELS.TITLE')}</h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{t('ARDI.CHANNELS.SUBTITLE')}</p>
        </div>

        {/* Your channels */}
        {!channels ? (
          <Skeleton className="h-28 rounded-2xl" />
        ) : channels.length > 0 ? (
          <section className="flex flex-col gap-3">
            <h2 className="text-sm font-medium">{t('ARDI.CHANNELS.YOURS')}</h2>
            <div className="grid gap-3 md:grid-cols-2" data-testid="channels-list">
              {channels.map((c) => {
                const phase = channelPhase(c);
                const Icon = CHANNEL_ICONS[c.kind];
                const badge = PHASE_BADGE[phase];
                return (
                  <button
                    key={c.uuid}
                    type="button"
                    onClick={() => openChannel(c.uuid)}
                    className="flex flex-col gap-3 rounded-2xl border bg-card p-4 text-start transition-colors hover:border-foreground/25"
                    data-testid={`channel-${c.kind}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <span className="flex min-w-0 items-center gap-3">
                        <span className="flex size-9 shrink-0 items-center justify-center rounded-lg border bg-background text-muted-foreground">
                          <Icon className="size-4" />
                        </span>
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-medium">{c.name}</span>
                          <span className="block truncate text-xs text-muted-foreground">
                            {c.info?.botUsername ? `@${c.info.botUsername}` : c.info?.team || c.info?.phone || t(channelKindKey(c.kind))}
                          </span>
                        </span>
                      </span>
                      <Badge variant={badge.variant} className="shrink-0 gap-1.5">
                        {badge.dot && <span className={cn('size-1.5 rounded-full', badge.dot)} aria-hidden />}
                        {t(`ARDI.CHANNELS.PHASE.${phase.toUpperCase()}`)}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      {c.kind !== 'webhook' && <span>{t('ARDI.CHANNELS.CHATS', { count: c.links.length })}</span>}
                      <span>{t('ARDI.CHANNELS.MESSAGES', { in: c.stats.messagesIn, out: c.stats.messagesOut })}</span>
                      <span>{t('ARDI.CHANNELS.ALERTS', { count: c.notifyTypes.length })}</span>
                      {c.stats.lastActivityAt && (
                        <span>
                          {t('ARDI.CHANNELS.LAST_ACTIVE')} <RelativeTime value={c.stats.lastActivityAt} />
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </section>
        ) : null}

        {/* Add a channel */}
        <section className="flex flex-col gap-3">
          <h2 className="text-sm font-medium">{t(channels?.length ? 'ARDI.CHANNELS.ADD_ANOTHER' : 'ARDI.CHANNELS.ADD')}</h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {CHANNEL_KINDS.map((kind) => {
              const Icon = CHANNEL_ICONS[kind];
              const count = byKind.get(kind)?.length ?? 0;
              return (
                <Card key={kind} className="gap-0 py-0" data-testid={`add-${kind}`}>
                  <div className="flex h-full flex-col gap-3 p-4">
                    <span className="flex size-9 items-center justify-center rounded-lg border bg-background text-muted-foreground">
                      <Icon className="size-4" />
                    </span>
                    <div>
                      <p className="text-sm font-medium">{t(channelKindKey(kind))}</p>
                      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{t(`ARDI.CHANNELS.KIND_DESC.${kind.toUpperCase()}`)}</p>
                    </div>
                    <div className="mt-auto flex items-center justify-between gap-2 pt-1">
                      <span className="text-[11px] text-muted-foreground">{t(`ARDI.CHANNELS.KIND_TIME.${kind.toUpperCase()}`)}</span>
                      <Button variant="outline" size="sm" onClick={() => add(kind)} disabled={!!creating}>
                        {creating === kind ? <Loader2 className="animate-spin" /> : null}
                        {t(count ? 'ARDI.CHANNELS.ADD_ONE_MORE' : 'ARDI.CHANNELS.SET_UP')}
                        {creating !== kind && <ArrowRight data-icon="inline-end" className="rtl:rotate-180" />}
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </section>

        {/* What linking means, in one place, before anyone links anything. */}
        <div className="flex items-start gap-3 rounded-2xl border p-4">
          <ShieldCheck className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
          <div className="flex flex-col gap-1 text-xs leading-relaxed text-muted-foreground">
            <p className="text-sm text-foreground">{t('ARDI.CHANNELS.SAFETY_TITLE')}</p>
            <p>{t('ARDI.CHANNELS.SAFETY_1')}</p>
            <p>{t('ARDI.CHANNELS.SAFETY_2')}</p>
            <p>{t('ARDI.CHANNELS.SAFETY_3')}</p>
          </div>
        </div>
      </div>

      <SidePanel
        open={!!open}
        onOpenChange={(o) => !o && openChannel(null)}
        title={open ? open.name : ''}
        description={open ? t(channelPhase(open) === 'setup' ? `ARDI.CHANNELS.SETUP.TITLE_${open.kind.toUpperCase()}` : `ARDI.CHANNELS.KIND_DESC.${open.kind.toUpperCase()}`) as string : undefined}
        width="data-[side=right]:sm:max-w-[640px]"
      >
        {open && (channelPhase(open) === 'setup' ? (
          <div className="flex flex-col gap-4">
            <ChannelSetup
              channel={open}
              onConnected={(c) => {
                replace(c);
                toast.success(t('ARDI.CHANNELS.SETUP.CONNECTED', { name: c.name }));
              }}
            />
            {/* Changed your mind: nothing was connected yet, so this just removes the draft. */}
            <Button
              variant="ghost"
              size="sm"
              className="self-start text-muted-foreground"
              onClick={async () => {
                await api.removeChannel(open.uuid).catch(() => undefined);
                setChannels((list) => list?.filter((x) => x.uuid !== open.uuid) ?? list);
                openChannel(null);
              }}
            >
              {t('ARDI.CHANNELS.SETUP.CANCEL')}
            </Button>
          </div>
        ) : (
          <ChannelManage
            channel={open}
            onChange={replace}
            onRemoved={() => {
              setChannels((list) => list?.filter((x) => x.uuid !== open.uuid) ?? list);
              openChannel(null);
            }}
          />
        ))}
      </SidePanel>
    </PageContainer>
  );
};

export default ChannelsPage;
